import { deleteReading, getEffectiveLevel, listLearningSteps, listReadings } from "../db";
import { downloadReadingReportById } from "../export/report";
import { LEVEL_LABELS } from "../level";
import { navigate } from "../router";
import type { Route } from "../types";
import { uiConfirm } from "../ui/dialog";
import { getViewEl } from "../ui/layout";
import { esc, toast } from "../ui/toast";

const STATUS_LABEL: Record<string, string> = {
  draft: "草稿",
  diagnosing: "诊断中",
  learning: "学习中",
  done: "已读懂",
};

function stepProgressLabel(
  status: string,
  currentStep: number,
  totalSteps: number,
): string | null {
  if (totalSteps === 0) {
    return null;
  }
  if (status === "done") {
    return `步骤 ${totalSteps}/${totalSteps}`;
  }
  return `步骤 ${Math.min(currentStep + 1, totalSteps)}/${totalSteps}`;
}

export async function renderLibraryPage(_route: Route): Promise<void> {
  const view = getViewEl();
  if (!view) {
    return;
  }

  const readings = await listReadings();
  const effectiveLevel = await getEffectiveLevel();
  const stepGroups = await Promise.all(readings.map((r) => listLearningSteps(r.id)));
  const stepCountByReading = new Map(
    readings.map((reading, index) => [reading.id, stepGroups[index]?.length ?? 0]),
  );

  const listHtml = readings.length
    ? `<ul class="reading-list">
        ${readings
          .map((item) => {
            const totalSteps = stepCountByReading.get(item.id) ?? 0;
            const progress = stepProgressLabel(item.status, item.currentStep, totalSteps);
            return `
          <li class="reading-item" data-id="${esc(item.id)}">
            <div class="reading-item-body">
              <h3>${esc(item.title)}</h3>
              <p class="muted">${esc(item.text.slice(0, 140))}${item.text.length > 140 ? "…" : ""}</p>
              <p class="meta">
                <span class="badge badge-status badge-${esc(item.status)}">${esc(STATUS_LABEL[item.status] ?? item.status)}</span>
                ${progress ? `<span class="badge badge-progress">${esc(progress)}</span>` : ""}
                <span>${esc(item.updatedAt.slice(0, 19).replace("T", " "))}</span>
              </p>
            </div>
            <div class="reading-item-actions">
              <button type="button" class="btn btn-sm" data-action="open">打开</button>
              <button type="button" class="btn btn-sm" data-action="edit">编辑</button>
              <button type="button" class="btn btn-sm" data-action="export">导出</button>
              <button type="button" class="btn btn-sm btn-danger" data-action="delete">删除</button>
            </div>
          </li>`;
          })
          .join("")}
      </ul>`
    : `<div class="empty-state">
        <h2>还没有阅读记录</h2>
        <p class="lead">去阅读页粘贴一段英文，或从 Excel 导入历史数据。</p>
        <a class="btn btn-primary" href="#/read">开始阅读</a>
      </div>`;

  view.innerHTML = `
    <div class="page-head">
      <div>
        <h1>文库</h1>
        <p class="lead">本机保存的阅读记录与辅导进度。当前评估水平：<span class="badge badge-level">${esc(LEVEL_LABELS[effectiveLevel])}</span></p>
      </div>
      <button type="button" class="btn" id="btnNewRead">新建阅读</button>
    </div>
    <section class="panel">${listHtml}</section>
  `;

  view.querySelector<HTMLButtonElement>("#btnNewRead")?.addEventListener("click", () => {
    navigate("read");
  });

  view.querySelectorAll<HTMLElement>(".reading-item").forEach((item) => {
    const id = item.dataset.id;
    if (!id) {
      return;
    }

    item.querySelector<HTMLButtonElement>('[data-action="open"]')?.addEventListener("click", () => {
      navigate("diagnosis", null, id);
    });

    item.querySelector<HTMLButtonElement>('[data-action="edit"]')?.addEventListener("click", () => {
      navigate("read", null, id);
    });

    item.querySelector<HTMLButtonElement>('[data-action="export"]')?.addEventListener("click", async () => {
      try {
        await downloadReadingReportById(id);
        toast("已导出 Markdown");
      } catch (err) {
        toast(err instanceof Error ? err.message : "导出失败");
      }
    });

    item.querySelector<HTMLButtonElement>('[data-action="delete"]')?.addEventListener("click", async () => {
      const title = item.querySelector("h3")?.textContent?.trim() || "该记录";
      const ok = await uiConfirm({
        title: "删除记录",
        message: `确定删除「${title}」？此操作不可恢复。`,
        confirmLabel: "删除",
        danger: true,
      });
      if (!ok) {
        return;
      }
      try {
        await deleteReading(id);
        toast("已删除");
        await renderLibraryPage({ name: "library", focus: null, readingId: null });
      } catch (err) {
        toast(err instanceof Error ? err.message : "删除失败");
      }
    });
  });
}
